declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const GA_ONCE_PREFIX = 'valvur-welcome:ga:';

/**
 * Load GA4 and send the initial page_view.
 * Uses Google's official dataLayer stub (`push(arguments)`, not arrays) —
 * array pushes show up in dataLayer but may never be sent to GA.
 */
export function initAnalytics(): void {
  if (!MEASUREMENT_ID || typeof document === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  // Must match Google's snippet exactly — do not push rest-parameter arrays
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());

  const debugMode =
    typeof location !== 'undefined' &&
    new URLSearchParams(location.search).has('ga_debug');

  window.gtag('config', MEASUREMENT_ID, {
    send_page_view: true,
    ...(debugMode ? { debug_mode: true } : {}),
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(script);
}

export function trackEvent(
  name: string,
  params?: Record<string, string | number | boolean>,
): void {
  if (!MEASUREMENT_ID || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params);
}

function oncePerSession(key: string, fn: () => void): void {
  try {
    const storageKey = GA_ONCE_PREFIX + key;
    if (sessionStorage.getItem(storageKey) === '1') return;
    sessionStorage.setItem(storageKey, '1');
  } catch {
    // sessionStorage unavailable — still send the event
  }
  fn();
}

/** Step checkbox marked done (step1–step4). */
export function trackStepComplete(stepId: string): void {
  oncePerSession(`step:${stepId}`, () => {
    trackEvent('step_complete', { step_id: stepId });
  });
}

/** User reached the finish / feedback section. */
export function trackGuideFinishView(): void {
  oncePerSession('finish', () => {
    trackEvent('guide_finish_view');
  });
}

/** Bind click tracking for the Google Forms feedback CTA. */
export function bindFeedbackFormTracking(): void {
  const link = document.getElementById('feedbackFormCta');
  if (!link) return;

  link.addEventListener('click', () => {
    trackEvent('feedback_form_open', {
      link_url: (link as HTMLAnchorElement).href,
      cta_id: 'feedbackFormCta',
    });
  });
}

/** Product entry points, step completion, and finish section visibility. */
export function bindGuideTracking(): void {
  const familyHub = document.querySelector<HTMLAnchorElement>('.web-app-btn');
  familyHub?.addEventListener('click', () => {
    trackEvent('family_hub_open', { link_url: familyHub.href });
  });

  const appStore = document.querySelector<HTMLAnchorElement>('.store-badge--apple');
  appStore?.addEventListener('click', () => {
    trackEvent('app_store_open', { link_url: appStore.href });
  });

  const playStore = document.querySelector<HTMLAnchorElement>('.store-badge--google');
  playStore?.addEventListener('click', () => {
    trackEvent('play_store_open', { link_url: playStore.href });
  });

  document.querySelectorAll<HTMLInputElement>('[data-progress]').forEach((box) => {
    box.addEventListener('change', () => {
      if (!box.checked) return;
      const stepId = box.getAttribute('data-progress');
      if (stepId) trackStepComplete(stepId);
    });
  });

  const finish = document.querySelector('section[data-screen="finish"]');
  if (finish) {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          trackGuideFinishView();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(finish);
  }
}
